import { forwardRef, useState, useEffect } from 'react';
import { cn } from '../../lib/utils';
import { patterns, formatters } from '../../lib/validation';

interface FormInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  error?: boolean;
  validationError?: string;
}

interface FormTextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  error?: boolean;
  validationError?: string;
}

interface FormSelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  error?: boolean;
  validationError?: string;
}

// Base Input Component
const FormInput = forwardRef<HTMLInputElement, FormInputProps>(
  ({ className, error, validationError, ...props }, ref) => {
    const inputClassName = cn(
      'mt-1 block w-full rounded-md shadow-sm sm:text-sm transition-colors duration-200',
      error
        ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
        : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500',
      className
    );

    return (
      <div>
        <input ref={ref} className={inputClassName} {...props} />
        {validationError && <p className="mt-1 text-sm text-red-600">{validationError}</p>}
      </div>
    );
  }
);
FormInput.displayName = 'FormInput';

// Textarea Component
const FormTextarea = forwardRef<HTMLTextAreaElement, FormTextareaProps>(
  ({ className, error, validationError, ...props }, ref) => {
    const textareaClassName = cn(
      'mt-1 block w-full rounded-md shadow-sm sm:text-sm transition-colors duration-200',
      error
        ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
        : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500',
      className
    );

    return (
      <div>
        <textarea ref={ref} className={textareaClassName} {...props} />
        {validationError && <p className="mt-1 text-sm text-red-600">{validationError}</p>}
      </div>
    );
  }
);
FormTextarea.displayName = 'FormTextarea';

// Select Component
const FormSelect = forwardRef<HTMLSelectElement, FormSelectProps>(
  ({ className, error, validationError, ...props }, ref) => {
    const selectClassName = cn(
      'mt-1 block w-full rounded-md shadow-sm sm:text-sm transition-colors duration-200',
      error
        ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
        : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500',
      className
    );

    return (
      <div>
        <select ref={ref} className={selectClassName} {...props} />
        {validationError && <p className="mt-1 text-sm text-red-600">{validationError}</p>}
      </div>
    );
  }
);
FormSelect.displayName = 'FormSelect';

// CPF/CNPJ Input
interface FormCpfCnpjProps extends FormInputProps {
  type: 'cpf' | 'cnpj';
}

const FormCpfCnpj = forwardRef<HTMLInputElement, FormCpfCnpjProps>(
  ({ type, onChange, value, ...props }, ref) => {
    const [inputValue, setInputValue] = useState(value?.toString() || '');

    useEffect(() => {
      setInputValue(value?.toString() || '');
    }, [value]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = formatters[type](e.target.value);
      setInputValue(newValue);

      if (onChange) {
        e.target.value = newValue;
        onChange(e);
      }
    };

    return (
      <FormInput
        ref={ref}
        value={inputValue}
        onChange={handleChange}
        pattern={type === 'cpf' ? patterns.cpf.source : patterns.cnpj.source}
        {...props}
      />
    );
  }
);
FormCpfCnpj.displayName = 'FormCpfCnpj';

// Phone Input
const FormPhone = forwardRef<HTMLInputElement, FormInputProps>(
  ({ onChange, value, ...props }, ref) => {
    const [inputValue, setInputValue] = useState(value?.toString() || '');

    useEffect(() => {
      setInputValue(value?.toString() || '');
    }, [value]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = formatters.phone(e.target.value);
      setInputValue(newValue);

      if (onChange) {
        e.target.value = newValue;
        onChange(e);
      }
    };

    return (
      <FormInput
        ref={ref}
        type="tel"
        value={inputValue}
        onChange={handleChange}
        pattern={patterns.phone.source}
        {...props}
      />
    );
  }
);
FormPhone.displayName = 'FormPhone';

// CEP Input
const FormCEP = forwardRef<HTMLInputElement, FormInputProps>(
  ({ onChange, value, ...props }, ref) => {
    const [inputValue, setInputValue] = useState(value?.toString() || '');

    useEffect(() => {
      setInputValue(value?.toString() || '');
    }, [value]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = formatters.cep(e.target.value);
      setInputValue(newValue);

      if (onChange) {
        e.target.value = newValue;
        onChange(e);
      }
    };

    return (
      <FormInput
        ref={ref}
        value={inputValue}
        onChange={handleChange}
        pattern={patterns.cep.source}
        {...props}
      />
    );
  }
);
FormCEP.displayName = 'FormCEP';

// Email Input
const FormEmail = forwardRef<HTMLInputElement, FormInputProps>(
  ({ onChange, value, ...props }, ref) => {
    const [inputValue, setInputValue] = useState(value?.toString() || '');

    useEffect(() => {
      setInputValue(value?.toString() || '');
    }, [value]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value);

      if (onChange) {
        onChange(e);
      }
    };

    return (
      <FormInput
        ref={ref}
        type="email"
        value={inputValue}
        onChange={handleChange}
        pattern={patterns.email.source}
        {...props}
      />
    );
  }
);
FormEmail.displayName = 'FormEmail';

export {
  FormInput,
  FormTextarea,
  FormSelect,
  FormCpfCnpj,
  FormPhone,
  FormCEP,
  FormEmail,
  FormEmail as FormMail, // Export FormEmail as both FormEmail and FormMail
};
